<template>
  <div>
    <v-card>
      <v-card-title>
        <v-icon color="primary">mdi-history</v-icon>
        <span class="px-2">{{ $t('activity_log') }}</span>
        <info-tool-tip class="ml-2" :message="$t('activity_log_hint')"></info-tool-tip>
        <v-spacer></v-spacer>
        <v-btn outlined color="secondary" :disabled="rows.length === 0" @click="exportRows">
          <v-icon class="me-1" small>mdi-download</v-icon>
          {{ $t('export_csv') }}
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-select
              v-model="action"
              :items="actionChoices"
              item-text="text"
              item-value="value"
              :label="$t('action')"
              clearable
              dense
            ></v-select>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="search"
              :label="$t('Search')"
              clearable
              dense
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- server side: this table only ever grows, so it is paged from the
           first row rather than shipped whole to the browser -->
      <v-data-table
        :headers="translatedHeaders"
        :items="rows"
        :loading="isLoading"
        :options.sync="options"
        :server-items-length="total"
        :footer-props="{ itemsPerPageOptions: [25, 50, 100], itemsPerPageText: $t('rows_per_page') }"
        :no-data-text="$t('noData')"
        item-key="id"
        disable-sort
      >
        <template v-slot:item.created_at="{ item }">
          <small>{{ item.created_at | moment("LL") }}</small> -
          <small class="text-muted">{{ item.created_at | moment("LT") }}</small>
        </template>

        <template v-slot:item.action="{ item }">
          <v-chip small dark :color="actionColor(item.action)">
            {{ actionText(item.action) }}
          </v-chip>
        </template>

        <template v-slot:item.actor_name="{ item }">
          {{ item.actor_name || $t('unknown') }}
          <small class="text-muted">{{ roleText(item.actor_role) }}</small>
        </template>

        <template v-slot:item.subject_label="{ item }">
          {{ item.subject_label || '-' }}
        </template>

        <template v-slot:item.meta="{ item }">
          <span v-if="metaText(item)" class="text-caption">{{ metaText(item) }}</span>
          <span v-else>-</span>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
/*
 * What the admins did.
 *
 * The panel can now delete accounts, set other people's passwords and move
 * coins about; before this there was nowhere to look afterwards and ask who.
 * A deleted row leaves nothing behind, and the Laravel log holds stack traces
 * rather than decisions.
 */
import InfoToolTip from "@/components/InfoToolTip";
import { exportRowsToCsv } from "@/utils/download";

export default {
  components: { InfoToolTip },
  data() {
    return {
      rows: [],
      total: 0,
      isLoading: false,
      action: null,
      search: "",
      searchTimer: null,
      knownActions: [],
      options: { page: 1, itemsPerPage: 25 },
      headers: [
        { text: "when", value: "created_at" },
        { text: "action", value: "action" },
        { text: "by", value: "actor_name" },
        { text: "on", value: "subject_label" },
        { text: "details", value: "meta" },
      ],
    };
  },
  computed: {
    translatedHeaders() {
      return this.headers.map((header) => ({
        ...header,
        text: this.$t(header.text),
      }));
    },
    actionChoices() {
      return this.knownActions.map((value) => ({
        value: value,
        text: this.actionText(value),
      }));
    },
  },
  watch: {
    options: {
      handler() {
        this.load();
      },
      deep: true,
    },
    action() {
      this.resetToFirstPage();
    },
    search() {
      //typing should not fire a request per keystroke
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.resetToFirstPage(), 400);
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    resetToFirstPage() {
      if (this.options.page !== 1) {
        //the watcher on options reloads
        this.options.page = 1;
        return;
      }
      this.load();
    },
    load() {
      this.isLoading = true;
      axios
        .get("/activity-logs/all", {
          params: {
            page: this.options.page,
            per_page: this.options.itemsPerPage,
            action: this.action || undefined,
            search: this.search || undefined,
          },
        })
        .then((response) => {
          this.rows = response.data.data || [];
          this.total = response.data.total || 0;
          this.knownActions = response.data.actions || [];
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_retrieving_activity"));
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    //user.deleted -> "User deleted", and translated where a string exists
    actionText(action) {
      if (!action) return "";
      const key = "activity_" + action.replace(".", "_");
      const translated = this.$t(key);
      if (translated !== key) return translated;

      const words = action.replace(".", " ").replace(/_/g, " ");
      return words.charAt(0).toUpperCase() + words.slice(1);
    },
    actionColor(action) {
      if (!action) return "primary";
      if (action.indexOf("deleted") !== -1 || action.indexOf("rejected") !== -1) return "error";
      if (action.indexOf("suspended") !== -1) return "warning";
      if (action.indexOf("created") !== -1 || action.indexOf("approved") !== -1) return "success";
      return "primary";
    },
    roleText(roleId) {
      return this.$t(
        { 1: "admin", 2: "school", 3: "driver", 4: "parent", 5: "guardian" }[roleId] || "unknown"
      );
    },
    metaText(row) {
      if (!row.meta) return "";
      return Object.keys(row.meta)
        .filter((key) => row.meta[key] !== null && row.meta[key] !== "" && row.meta[key] !== false)
        .map((key) => `${key.replace(/_/g, " ")}: ${row.meta[key]}`)
        .join(", ");
    },
    exportRows() {
      exportRowsToCsv(
        this.rows,
        [
          { text: this.$t("when"), value: (row) => this.$moment(row.created_at).format("YYYY-MM-DD HH:mm") },
          { text: this.$t("action"), value: (row) => this.actionText(row.action) },
          { text: this.$t("by"), value: "actor_name" },
          { text: this.$t("on"), value: "subject_label" },
          { text: this.$t("details"), value: (row) => this.metaText(row) },
        ],
        "activity-log"
      );
    },
  },
};
</script>
